import React from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import { formatMoney } from '../../Shared/Formatters';
import { useThemePalette } from '../../hooks/useThemePalette';

// Teal-forward palette to match the V3 vibe (brand teal leads the dominant slice).
// Premium skins override this live via useThemePalette (returns null in classic).
const COLORS = ['#06B6D4', '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444'];

/**
 * Donut / pie chart widget.
 * Handles: Category Distribution, F&B Dine-In vs Takeaway, Delivery Partner Breakdown.
 */
export default function DonutWidget({ data }) {
    const { t } = useTranslation();
    const palette = useThemePalette();
    if (!data?.data) return null;
    const d = data.data;
    const colors = palette || COLORS;

    let labels = [], values = [];
    if (Array.isArray(d)) {
        // Category distribution shape: [{category, totalSales, sharePct}, ...]
        labels = d.map(r => r.category || r.channel || r.name || '—');
        values = d.map(r => r.totalSales || r.sales || r.value || 0);
    } else if (d.dineIn !== undefined && d.takeaway !== undefined) {
        labels = [t('Dine-In'), t('Takeaway')];
        values = [d.dineIn, d.takeaway];
    } else {
        return <div style={{ padding: 20 }} className="v3-body">{t('Unrecognized data shape')}</div>;
    }

    if (!values.length || values.every(v => !v)) {
        return <div style={{ padding: 20 }} className="v3-body">{t('No data')}</div>;
    }

    const options = {
        chart: {
            type: 'donut',
            fontFamily: 'Inter, system-ui',
            // Soft depth so the ring lifts off the glass card.
            dropShadow: { enabled: true, top: 2, blur: 6, opacity: 0.12 },
        },
        labels,
        colors,
        legend: {
            position: 'right',
            fontSize: '12px',
            fontFamily: 'Inter, system-ui',
            labels: { colors: 'var(--v3-text-secondary)' },
            markers: { width: 9, height: 9, radius: 3, offsetX: -2 },
            itemMargin: { vertical: 4 },
        },
        dataLabels: {
            enabled: true,
            // Plain share — no leading "+" (formatPct adds one, wrong for parts-of-whole).
            formatter: (val) => `${Number(val).toFixed(1)}%`,
            style: { fontSize: '12px', fontWeight: 700, fontFamily: 'Inter, system-ui', colors: ['#ffffff'] },
            dropShadow: { enabled: true, blur: 2, opacity: 0.45 },
        },
        plotOptions: {
            pie: {
                // Pull the slice % labels inward onto the ring band so they don't
                // spill outside the donut / get clipped by the card edge.
                dataLabels: { offset: -16, minAngleToShowLabel: 8 },
                expandOnClick: false,
                donut: {
                    size: '70%',
                    labels: {
                        show: true,
                        name: {
                            fontSize: '13px',
                            fontFamily: 'Inter, system-ui',
                            color: 'var(--v3-text-muted)',
                            offsetY: -4,
                        },
                        value: {
                            fontSize: '16px',
                            fontWeight: 700,
                            fontFamily: 'Inter, system-ui',
                            color: 'var(--v3-text-primary)',
                            offsetY: 4,
                            formatter: (val) => formatMoney(val),
                        },
                        total: {
                            show: true,
                            label: t('Total'),
                            color: 'var(--v3-text-muted)',
                            fontSize: '13px',
                            fontWeight: 600,
                            formatter: (w) => formatMoney(w.globals.seriesTotals.reduce((a, b) => a + b, 0)),
                        },
                    },
                },
            },
        },
        // Transparent gaps between slices read as thin separators on the glass card.
        stroke: { width: 2, colors: ['transparent'] },
        tooltip: {
            // Custom glass tooltip — replaces the clunky default box, formats money,
            // adds the share. Concrete colours (renders in a body portal, outside .v3
            // where the theme CSS vars don't resolve).
            custom: ({ series, seriesIndex, w }) => {
                const label = w.globals.labels[seriesIndex] ?? '';
                const val = series[seriesIndex] ?? 0;
                const total = series.reduce((a, b) => a + (b || 0), 0);
                const pct = total ? (val / total) * 100 : 0;
                const color = w.globals.colors[seriesIndex] || '#06B6D4';
                return (
                    `<div style="padding:9px 12px;border-radius:10px;background:rgba(15,23,42,0.94);`
                    + `border:1px solid rgba(255,255,255,0.12);box-shadow:0 10px 28px rgba(0,0,0,0.4);`
                    + `font-family:Inter,system-ui;min-width:132px;">`
                    + `<div style="display:flex;align-items:center;gap:7px;margin-bottom:5px;">`
                    + `<span style="width:9px;height:9px;border-radius:2px;background:${color};box-shadow:0 0 8px ${color};"></span>`
                    + `<span style="color:#E2E8F0;font-size:12px;font-weight:600;">${label}</span>`
                    + `</div>`
                    + `<div style="color:#F8FAFC;font-size:15px;font-weight:700;letter-spacing:0.2px;">${formatMoney(val)}</div>`
                    + `<div style="color:#94A3B8;font-size:11px;margin-top:2px;">${t('{{pct}}% of total', { pct: pct.toFixed(1) })}</div>`
                    + `</div>`
                );
            },
        },
    };

    return (
        <div style={{ padding: '4px 10px 8px', height: '100%' }}>
            <Chart type="donut" options={options} series={values} height="100%" />
        </div>
    );
}
